import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { useApi } from "../../hooks/useApi";
import { web, errMsg, toList } from "../../lib/api";
import { PageHeader, Panel, Loader, ErrorNote, Empty, Btn, shortDate } from "../../components/app/kit";

const COLS = [
  ["fg2m", "2PM"], ["fg2a", "2PA"], ["fg3m", "3PM"], ["fg3a", "3PA"], ["ftm", "FTM"], ["fta", "FTA"],
  ["oreb", "OREB"], ["dreb", "DREB"], ["ast", "AST"], ["stl", "STL"], ["blk", "BLK"], ["tov", "TOV"], ["pf", "PF"],
];
const PAIRS = [["fg2m", "fg2a", "2s"], ["fg3m", "fg3a", "3s"], ["ftm", "fta", "free throws"]];
const n = (v) => Number(v) || 0;
const ptsOf = (l = {}) => 2 * n(l.fg2m) + 3 * n(l.fg3m) + n(l.ftm);
const pick = (l = {}) => Object.fromEntries(COLS.map(([k]) => [k, n(l[k])]));

const Cell = ({ value, onChange, testid, bad }) => (
  <input
    type="number" inputMode="numeric" min={0} max={99} data-testid={testid}
    value={value === 0 ? "" : value} placeholder="0"
    onChange={(e) => onChange(Math.max(0, Math.min(99, parseInt(e.target.value, 10) || 0)))}
    onFocus={(e) => e.target.select()}
    className={`h-10 w-12 rounded-lg border bg-[#0A0E1A] text-center font-mono-gd text-sm text-white outline-none placeholder:text-slate-600 focus:border-[#39FF14] [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none ${bad ? "border-[#FF3B30]" : "border-[#1E293B]"}`}
  />
);

const ScoreInput = ({ label, value, onChange, testid, hint }) => (
  <label className="text-center">
    <span className="block font-mono-gd text-[11px] uppercase tracking-[0.2em] text-slate-400">{label}</span>
    <input type="number" min={0} data-testid={testid} value={value} placeholder="0" onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-28 rounded-xl border border-[#1E293B] bg-[#0A0E1A] py-2 text-center font-mono-gd text-4xl font-bold text-white outline-none focus:border-[#39FF14] [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none" />
    {hint && <span className="mt-1 block text-xs text-slate-400">{hint}</span>}
  </label>
);

export default function StatEntry() {
  const { gameId } = useParams();
  const [params] = useSearchParams();
  const focus = params.get("player");
  const navigate = useNavigate();
  const { membership, inLevel } = useAuth();
  const tid = membership?.team_id;
  const game = useApi(tid && `/teams/${tid}/games/${gameId}`);
  const roster = useApi(tid && `/teams/${tid}/players`);
  const g = game.data?.game ?? game.data ?? {};
  const players = useMemo(() => toList(roster.data, ["players"]).filter((p) => p.status !== "inactive" && (inLevel(p) || p.id === focus))
    .sort((a, b) => (parseInt(a.jersey, 10) || 999) - (parseInt(b.jersey, 10) || 999)), [roster.data, inLevel, focus]);
  const [lines, setLines] = useState({});
  const [us, setUs] = useState("");
  const [them, setThem] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!game.data) return;
    const src = g.lines ?? {};
    setLines(Object.fromEntries(Object.entries(src).map(([pid, l]) => [pid, pick(l)])));
    setUs(g.final_score_us ?? "");
    setThem(g.final_score_them ?? "");
  }, [game.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const ready = !!game.data && !!roster.data;
  useEffect(() => {
    if (!ready || !focus) return undefined;
    const t = setTimeout(() => {
      const row = document.querySelector(`[data-testid="stat-row-${focus}"]`);
      row?.scrollIntoView({ behavior: "smooth", block: "center" });
      row?.querySelector("input")?.focus({ preventScroll: true });
    }, 150);
    return () => clearTimeout(t);
  }, [ready, focus]);

  const set = (pid, k) => (v) => setLines((s) => ({ ...s, [pid]: { ...pick(s[pid]), [k]: v } }));
  const total = players.reduce((sum, p) => sum + ptsOf(lines[p.id]), 0);
  const badPair = (l, m, a) => n(l?.[m]) > n(l?.[a]);

  const save = async () => {
    for (const p of players) for (const [m, a, label] of PAIRS) {
      if (badPair(lines[p.id], m, a)) return toast.error(`${p.name}: made ${label} can't be more than attempts`);
    }
    if (them === "" || them === null) return toast.error("Enter the opponent's final score");
    setBusy(true);
    try {
      const body = { lines: Object.fromEntries(players.map((p) => [p.id, pick(lines[p.id])])), final_score_us: us === "" ? null : Number(us), final_score_them: Number(them) };
      await web.post(`/teams/${tid}/games/${gameId}/box-score`, body);
      toast.success("Box score saved");
      navigate(`/coach/games/${gameId}`);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  if (game.loading || roster.loading) return <Loader />;
  if (game.error || roster.error) return <ErrorNote error={game.error || roster.error} />;

  return (
    <div>
      <Link to={`/coach/games/${gameId}`} data-testid="back-to-game" className="mb-6 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-[#39FF14]"><ArrowLeft className="h-4 w-4" />Game</Link>
      <PageHeader eyebrow={`${shortDate(g.date)} · Enter stats`} title={`vs ${g.opponent ?? "Opponent"}`}>
        <Btn data-testid="save-box-score-button" disabled={busy || !players.length} onClick={save}><Save className="h-4 w-4" />{busy ? "Saving…" : "Save box score"}</Btn>
      </PageHeader>
      <Panel className="mb-6 flex flex-wrap items-start justify-around gap-6 py-6">
        <ScoreInput label={membership?.team_name ?? "Us"} value={us} onChange={setUs} testid="final-score-us-input" hint={us === "" ? `Uses player points: ${total}` : total !== Number(us) ? `Player points add up to ${total}` : null} />
        <span className="self-center font-display text-3xl font-black text-slate-500">VS</span>
        <ScoreInput label={g.opponent ?? "Opponent"} value={them} onChange={setThem} testid="final-score-them-input" />
      </Panel>
      {!players.length ? <Empty title="No players on the roster" text="Add players on the Roster page first." /> : (
        <Panel className="p-0 sm:p-0">
          <div className="overflow-x-auto">
            <table data-testid="stat-entry-table" className="w-full min-w-[900px] text-left text-sm">
              <thead>
                <tr className="font-mono-gd text-[11px] uppercase tracking-wider text-slate-400">
                  <th className="sticky left-0 z-10 bg-[#0F172A] px-4 py-3">Player</th>
                  {COLS.map(([k, l]) => <th key={k} className="px-1 py-3 text-center">{l}</th>)}
                  <th className="px-4 py-3 text-center text-[#39FF14]">PTS</th>
                </tr>
              </thead>
              <tbody>
                {players.map((p) => (
                  <tr key={p.id} data-testid={`stat-row-${p.id}`} className={`border-t border-[#1E293B] ${p.id === focus ? "bg-[#39FF14]/10" : ""}`}>
                    <td className={`sticky left-0 z-10 max-w-[150px] px-4 py-2 ${p.id === focus ? "bg-[#14301A]" : "bg-[#0F172A]"}`}>
                      <span className="mr-2 font-mono-gd text-xs text-slate-400">#{p.jersey ?? "–"}</span>
                      <span className="font-semibold text-white">{p.name}</span>
                    </td>
                    {COLS.map(([k]) => (
                      <td key={k} className="px-1 py-2 text-center">
                        <Cell testid={`stat-${p.id}-${k}`} value={n(lines[p.id]?.[k])} onChange={set(p.id, k)}
                          bad={PAIRS.some(([m, a]) => (k === m || k === a) && badPair(lines[p.id], m, a))} />
                      </td>
                    ))}
                    <td data-testid={`stat-${p.id}-pts`} className="px-4 py-2 text-center font-mono-gd text-lg font-bold text-[#39FF14]">{ptsOf(lines[p.id])}</td>
                  </tr>
                ))}
                <tr className="border-t border-[#1E293B] font-mono-gd text-xs uppercase text-slate-400">
                  <td className="sticky left-0 z-10 bg-[#0F172A] px-4 py-3">Team</td>
                  {COLS.map(([k]) => <td key={k} className="px-1 py-3 text-center text-white">{players.reduce((s, p) => s + n(lines[p.id]?.[k]), 0)}</td>)}
                  <td data-testid="stat-team-pts" className="px-4 py-3 text-center text-lg font-bold text-[#39FF14]">{total}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Panel>
      )}
      <p className="mt-4 text-sm text-slate-400">Type each player's totals for the game. Attempts include makes. Saving updates season stats in the mobile app too.</p>
    </div>
  );
}
